import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import {
  Box,
  Card,
  Grid,
  Stack,
  Avatar,
  Button,
  IconButton,
  Typography,
  Divider,
  Chip,
  Tooltip,
  Skeleton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  Fab,
  Alert,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  ListItemIcon,
  CircularProgress,
  Menu,
} from "@mui/material";

import SportsSoccerIcon from "@mui/icons-material/SportsSoccer";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import GroupIcon from "@mui/icons-material/Group";
import SaveIcon from "@mui/icons-material/Save";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import SyncIcon from "@mui/icons-material/Sync";
import HomeIcon from "@mui/icons-material/Home";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import CloseIcon from "@mui/icons-material/Close";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import ShareIcon from "@mui/icons-material/Share";
import GridOnIcon from "@mui/icons-material/GridOn";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { jwtDecode } from "jwt-decode";

import apiFetch from "services/http";
import { useSeason } from "context/SeasonContext";
import {
  PageToolbar,
  ModalShell,
  ShareConvocatoriaDialog,
  ConfirmDialog,
  EmptyState,
  MedioCampoFutsal,
  PistaVolley,
  useNotify,
} from "components/Convokko";
import {
  contarConvocatoriasRecientes,
  contarAsistenciasEntrenamientoRecientes,
} from "utils/convocatoria";
import {
  esFutsal,
  SLOTS_FUTSAL,
  numTiemposPorDefecto,
  esVolley,
  slotsVolley,
  MODO_VOLLEY_POR_DEFECTO,
} from "utils/alineacion";

const MAX_CONVOCADOS_SUGERENCIA = 11;

const partidoVacio = (temporada) => ({
  _id: null,
  competicion: "",
  temporada,
  jornada: 1,
  fecha: "",
  hora: "",
  lugar: "",
  local: true,
  resultado: "",
  equipo_rival: "",
  id_equipo: "",
});

const cleanEmptyFields = (obj) => {
  const cleaned = {};
  Object.keys(obj).forEach((key) => {
    const value = obj[key];
    if (value !== null && value !== undefined && value !== "" && String(value).trim() !== "") {
      cleaned[key] = typeof value === "string" ? value.trim() : value;
    }
  });
  return cleaned;
};

const formatDate = (fecha) => {
  if (!fecha) return "Sin fecha";
  const d = new Date(fecha);
  if (Number.isNaN(d.getTime())) return fecha;
  return d.toLocaleDateString("es-ES", { weekday: "short", day: "2-digit", month: "short" });
};

const monthLabel = (fecha) => {
  const d = new Date(fecha);
  if (!fecha || Number.isNaN(d.getTime())) return "Sin fecha";
  const label = d.toLocaleDateString("es-ES", { month: "long", year: "numeric" });
  return label.charAt(0).toUpperCase() + label.slice(1);
};

const isToday = (fecha) => {
  if (!fecha) return false;
  const d = new Date(fecha);
  const hoy = new Date();
  return (
    d.getDate() === hoy.getDate() &&
    d.getMonth() === hoy.getMonth() &&
    d.getFullYear() === hoy.getFullYear()
  );
};

// Teja de fecha para las filas de la agenda: día grande + día de la semana
function DateTile({ fecha }) {
  const d = new Date(fecha);
  const valid = fecha && !Number.isNaN(d.getTime());
  const hoy = valid && isToday(fecha);
  const futuro = valid && !hoy && d > new Date();
  return (
    <Box
      sx={{
        width: 52,
        height: 52,
        borderRadius: "14px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        bgcolor: hoy ? "rgba(251,181,63,0.18)" : futuro ? "rgba(34,51,107,0.08)" : "grey.100",
        border: "1px solid",
        borderColor: hoy ? "primary.main" : "transparent",
      }}
    >
      <Typography
        variant="h6"
        fontWeight={800}
        lineHeight={1}
        color={hoy ? "#D97706" : futuro ? "#22336B" : "text.primary"}
      >
        {valid ? d.getDate() : "—"}
      </Typography>
      <Typography
        variant="caption"
        fontWeight={700}
        textTransform="uppercase"
        fontSize="0.6rem"
        color={hoy ? "#D97706" : "text.secondary"}
      >
        {hoy ? "HOY" : valid ? d.toLocaleDateString("es-ES", { weekday: "short" }) : "s/f"}
      </Typography>
    </Box>
  );
}

DateTile.propTypes = {
  fecha: PropTypes.string,
};

const resultadoInfo = (resultado, local) => {
  if (!resultado || resultado === "-") return null;
  const partes = resultado.split("-").map((g) => parseInt(g.trim(), 10));
  if (partes.length !== 2 || partes.some(Number.isNaN))
    return { color: "default", label: resultado };
  const [gl, gv] = partes;
  const propios = local ? gl : gv;
  const rival = local ? gv : gl;
  if (propios > rival) return { color: "success", label: resultado };
  if (propios < rival) return { color: "error", label: resultado };
  return { color: "warning", label: resultado };
};

// Agenda de partidos agrupada por mes, aislada en su propio componente
// memoizado: es la parte más pesada de renderizar (puede haber cientos de
// partidos), y no debe volver a reconciliarse cada vez que el usuario escribe
// en el formulario de crear/editar partido (que vive en el componente padre).
const AgendaPartidos = React.memo(function AgendaPartidos({
  grupos,
  equiposById,
  onOpenConvocatoria,
  onOpenAlineacion,
  onEdit,
  onDelete,
}) {
  const getEquipoName = (equipoId) => equiposById[equipoId]?.nombre || "Equipo desconocido";

  return (
    <Stack spacing={3}>
      {grupos.map((grupo) => (
        <Box key={grupo.label}>
          <Stack direction="row" alignItems="center" spacing={1.5} mb={1.25} px={0.5}>
            <Typography
              variant="subtitle2"
              fontWeight={800}
              textTransform="uppercase"
              letterSpacing="0.05em"
              color="text.secondary"
              fontSize="0.72rem"
            >
              {grupo.label}
            </Typography>
            <Chip
              label={grupo.items.length}
              size="small"
              sx={{ height: 18, fontSize: "0.65rem", fontWeight: 700 }}
            />
            <Box flexGrow={1} height="1px" bgcolor="grey.200" />
          </Stack>

          <Card
            elevation={0}
            sx={{
              borderRadius: 3,
              border: "1px solid",
              borderColor: "grey.200",
              overflow: "hidden",
            }}
          >
            {grupo.items.map((partido, idx) => {
              const res = resultadoInfo(partido.resultado, partido.local);
              const convocados = partido.asistencia?.jugadores?.length || 0;
              const esFutsalPartido = esFutsal(equiposById[partido.id_equipo]?.deporte);
              const esVolleyPartido = esVolley(equiposById[partido.id_equipo]?.deporte);
              return (
                <Box key={partido._id}>
                  {idx > 0 && <Divider />}
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={{ xs: 1.5, sm: 2 }}
                    px={{ xs: 1.5, sm: 2.5 }}
                    py={1.25}
                    sx={{
                      transition: "background .15s ease",
                      "&:hover": { bgcolor: "grey.100" },
                    }}
                  >
                    <DateTile fecha={partido.fecha} />

                    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <Tooltip title={partido.local ? "En casa" : "Fuera"}>
                          {partido.local ? (
                            <HomeIcon sx={{ fontSize: 15, color: "#D97706" }} />
                          ) : (
                            <FlightTakeoffIcon sx={{ fontSize: 15, color: "info.main" }} />
                          )}
                        </Tooltip>
                        <Typography variant="subtitle2" fontWeight={700} noWrap>
                          vs {partido.equipo_rival || "Rival por definir"}
                        </Typography>
                      </Stack>
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        flexWrap="wrap"
                        useFlexGap
                      >
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {getEquipoName(partido.id_equipo)}
                        </Typography>
                        {partido.hora && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            fontWeight={600}
                            sx={{ display: "inline-flex", alignItems: "center", gap: 0.4 }}
                          >
                            <AccessTimeIcon sx={{ fontSize: 13 }} /> {partido.hora}
                          </Typography>
                        )}
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontWeight={600}
                          display={{ xs: "none", sm: "inline" }}
                        >
                          J{partido.jornada || "?"}
                          {partido.competicion ? ` · ${partido.competicion}` : ""}
                        </Typography>
                        {partido.lugar && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            noWrap
                            display={{ xs: "none", md: "inline-flex" }}
                            sx={{ alignItems: "center", gap: 0.4, maxWidth: 220 }}
                          >
                            <LocationOnIcon sx={{ fontSize: 13 }} /> {partido.lugar}
                          </Typography>
                        )}
                      </Stack>
                    </Box>

                    {res ? (
                      <Chip
                        label={res.label}
                        color={res.color}
                        size="small"
                        sx={{ fontWeight: 700, minWidth: 52 }}
                      />
                    ) : (
                      <Chip
                        label="Pendiente"
                        size="small"
                        variant="outlined"
                        sx={{ fontWeight: 600, display: { xs: "none", sm: "inline-flex" } }}
                      />
                    )}

                    <Chip
                      icon={<GroupIcon sx={{ fontSize: "14px !important" }} />}
                      label={convocados}
                      size="small"
                      color={convocados > 0 ? "info" : "default"}
                      variant={convocados > 0 ? "filled" : "outlined"}
                      onClick={() => onOpenConvocatoria(partido)}
                      sx={{
                        fontWeight: 700,
                        cursor: "pointer",
                        display: { xs: "none", sm: "inline-flex" },
                      }}
                    />

                    <Stack direction="row" spacing={0.25} flexShrink={0}>
                      <Tooltip title="Convocatoria">
                        <IconButton
                          size="small"
                          onClick={() => onOpenConvocatoria(partido)}
                          sx={{ color: "info.main", display: { sm: "none" } }}
                        >
                          <GroupIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {(esFutsalPartido || esVolleyPartido) && (
                        <Tooltip title="Alineación por tiempos">
                          <IconButton
                            size="small"
                            onClick={() => onOpenAlineacion(partido)}
                            sx={{ color: "text.secondary", "&:hover": { color: "primary.main" } }}
                          >
                            <GridOnIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Editar">
                        <IconButton
                          size="small"
                          onClick={() => onEdit(partido)}
                          sx={{ color: "text.secondary", "&:hover": { color: "info.main" } }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Eliminar">
                        <IconButton
                          size="small"
                          onClick={() => onDelete(partido)}
                          sx={{ color: "text.secondary", "&:hover": { color: "error.main" } }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Stack>
                </Box>
              );
            })}
          </Card>
        </Box>
      ))}
    </Stack>
  );
});

AgendaPartidos.propTypes = {
  grupos: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      items: PropTypes.array,
    })
  ).isRequired,
  equiposById: PropTypes.object.isRequired,
  onOpenConvocatoria: PropTypes.func.isRequired,
  onOpenAlineacion: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

export default function PartidosList() {
  const notify = useNotify();
  const { temporada, temporadas } = useSeason();
  const location = useLocation();
  const navigate = useNavigate();

  const [partidos, setPartidos] = useState([]);
  const [equipos, setEquipos] = useState([]);
  const [jugadores, setJugadores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [error, setError] = useState("");
  const [userRole, setUserRole] = useState("entrenador");

  // Filtros
  const [searchTerm, setSearchTerm] = useState("");
  const [equipoFiltro, setEquipoFiltro] = useState("");
  const [competicionFiltro, setCompeticionFiltro] = useState("");
  const [statusFiltro, setStatusFiltro] = useState("");

  // Modal partido
  const [openModal, setOpenModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentPartido, setCurrentPartido] = useState(partidoVacio(temporada));
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Convocatoria
  const [openConvocatoria, setOpenConvocatoria] = useState(false);
  const [convocatoriaPartido, setConvocatoriaPartido] = useState(null);
  const [convocatoria, setConvocatoria] = useState({ jugadores: [], entrenadores: [] });
  const [loadingConvocatoria, setLoadingConvocatoria] = useState(false);
  const [selectedJugador, setSelectedJugador] = useState(null);
  const [openShare, setOpenShare] = useState(false);
  const [entrenamientos, setEntrenamientos] = useState([]);
  const [sugerencia, setSugerencia] = useState(null);

  // Alineación por tiempos (fútbol sala y volley)
  const [openAlineacion, setOpenAlineacion] = useState(false);
  const [alineacionPartido, setAlineacionPartido] = useState(null);
  const [numTiempos, setNumTiempos] = useState(2);
  const [modoVolley, setModoVolley] = useState(MODO_VOLLEY_POR_DEFECTO);
  const [tiempoActivo, setTiempoActivo] = useState(1);
  const [alineaciones, setAlineaciones] = useState([]);
  const [convocadosAlineacion, setConvocadosAlineacion] = useState([]);
  const [loadingAlineacion, setLoadingAlineacion] = useState(false);
  const [savingSlot, setSavingSlot] = useState(false);
  const [slotSeleccionado, setSlotSeleccionado] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);

  useEffect(() => {
    try {
      const decoded = jwtDecode(localStorage.getItem("token"));
      setUserRole(decoded.rol || "entrenador");
    } catch (e) {
      setUserRole("entrenador");
    }
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const [dataPartidos, dataEquipos, dataJugadores, dataEntrenamientos] = await Promise.all([
        apiFetch("/api/partidos/ver-partidos-equipo"),
        apiFetch("/api/equipos"),
        apiFetch("/api/jugadores/ver-jugadores-equipo"),
        apiFetch("/api/entrenamientos/ver-entrenamientos"),
      ]);
      setPartidos(Array.isArray(dataPartidos) ? dataPartidos : []);
      setEquipos(Array.isArray(dataEquipos) ? dataEquipos : []);
      setJugadores(Array.isArray(dataJugadores) ? dataJugadores : []);
      setEntrenamientos(Array.isArray(dataEntrenamientos) ? dataEntrenamientos : []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const equiposById = useMemo(() => {
    const map = {};
    equipos.forEach((e) => {
      map[e._id || e.id] = e;
    });
    return map;
  }, [equipos]);

  const jugadoresById = useMemo(() => {
    const map = {};
    jugadores.forEach((j) => {
      map[j._id || j.id] = j;
    });
    return map;
  }, [jugadores]);

  const equiposTemporada = useMemo(
    () => equipos.filter((e) => Number(e.temporada) === Number(temporada)),
    [equipos, temporada]
  );

  // Opciones de temporada para el partido: solo temporadas conocidas >= la
  // actual (no tiene sentido crear un partido en una temporada ya archivada).
  // Se añade la del partido en edición si por algún motivo no estuviera en la
  // lista, para no perderla al abrir el modal.
  const temporadasSeleccionables = useMemo(() => {
    const base = temporadas.filter((t) => Number(t) >= Number(temporada));
    const actual = Number(currentPartido.temporada);
    if (actual && !base.includes(actual)) {
      return [...base, actual].sort((a, b) => a - b);
    }
    return base;
  }, [temporadas, temporada, currentPartido.temporada]);

  const competiciones = useMemo(
    () =>
      [...new Set(partidos.map((p) => p.competicion).filter(Boolean))].sort((a, b) =>
        a.localeCompare(b)
      ),
    [partidos]
  );

  const getEquipoName = (equipoId) => equiposById[equipoId]?.nombre || "Equipo desconocido";

  // Filtro coherente: temporada global (por el campo del partido o por el equipo) + resto
  const filteredPartidos = useMemo(
    () =>
      partidos.filter((partido) => {
        const equipoDelPartido = equiposById[partido.id_equipo];
        const temporadaPartido = Number(partido.temporada) || Number(equipoDelPartido?.temporada);
        if (temporadaPartido && temporadaPartido !== Number(temporada)) return false;
        if (equipoFiltro && partido.id_equipo !== equipoFiltro) return false;
        if (competicionFiltro && partido.competicion !== competicionFiltro) return false;
        const jugado =
          partido.resultado && partido.resultado !== "-" && partido.resultado !== "0-0";
        if (statusFiltro === "Jugado" && !jugado) return false;
        if (statusFiltro === "Pendiente" && jugado) return false;
        if (searchTerm) {
          const q = searchTerm.toLowerCase();
          const target = `${partido.equipo_rival || ""} ${partido.competicion || ""} ${
            partido.lugar || ""
          } ${getEquipoName(partido.id_equipo)}`.toLowerCase();
          if (!target.includes(q)) return false;
        }
        return true;
      }),
    [partidos, equiposById, temporada, equipoFiltro, competicionFiltro, statusFiltro, searchTerm]
  );

  // Agenda: ordenados del más reciente al más antiguo y agrupados por mes
  const gruposPorMes = useMemo(() => {
    const ordenados = [...filteredPartidos].sort((a, b) => {
      const da = new Date(`${a.fecha || "1970-01-01"}T${a.hora || "00:00"}`);
      const db = new Date(`${b.fecha || "1970-01-01"}T${b.hora || "00:00"}`);
      return db - da;
    });
    const grupos = [];
    ordenados.forEach((p) => {
      const label = monthLabel(p.fecha);
      const ultimo = grupos[grupos.length - 1];
      if (ultimo && ultimo.label === label) {
        ultimo.items.push(p);
      } else {
        grupos.push({ label, items: [p] });
      }
    });
    return grupos;
  }, [filteredPartidos]);

  // --- CRUD partido ---
  const handleOpenCreate = () => {
    setEditMode(false);
    setCurrentPartido(partidoVacio(temporada));
    setOpenModal(true);
  };

  // useCallback: esta función se pasa como prop a AgendaPartidos (memoizado),
  // así que necesita mantener identidad estable entre renders para que el
  // memo funcione y no reconcilie la agenda al escribir en el formulario.
  const handleOpenEdit = useCallback(
    (partido) => {
      setEditMode(true);
      setCurrentPartido({ ...partidoVacio(partido.temporada || temporada), ...partido });
      setOpenModal(true);
    },
    [temporada]
  );

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCurrentPartido((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : name === "jornada" ? parseInt(value, 10) || 1 : value,
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const payload = cleanEmptyFields({
        competicion: currentPartido.competicion,
        temporada: Number(currentPartido.temporada) || Number(temporada),
        jornada: parseInt(currentPartido.jornada, 10) || 1,
        fecha: currentPartido.fecha,
        hora: currentPartido.hora,
        lugar: currentPartido.lugar,
        local: Boolean(currentPartido.local),
        equipo_rival: currentPartido.equipo_rival,
        resultado: currentPartido.resultado,
        ...(editMode && currentPartido._id && { _id: currentPartido._id }),
        ...(currentPartido.id_equipo && { id_equipo: currentPartido.id_equipo }),
      });
      // local: false se pierde con cleanEmptyFields; lo forzamos siempre
      payload.local = Boolean(currentPartido.local);

      if (editMode) {
        await apiFetch("/api/partidos/actualizar-partido-equipo", { method: "PUT", body: payload });
        notify.success("Partido actualizado");
      } else {
        await apiFetch("/api/partidos/crear-partido-equipo", { method: "POST", body: payload });
        notify.success("Partido creado");
      }
      setOpenModal(false);
      await fetchData();
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setSaving(true);
      await apiFetch("/api/partidos/eliminar-partido-equipo", {
        method: "DELETE",
        params: { partido_id: deleteTarget._id },
      });
      setPartidos((prev) => prev.filter((p) => p._id !== deleteTarget._id));
      notify.success("Partido eliminado");
      setDeleteTarget(null);
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSincronizar = async () => {
    try {
      setSyncLoading(true);
      await apiFetch("/api/externas/sincronizar", { method: "POST" });
      notify.success("Partidos IMD sincronizados");
      await fetchData();
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSyncLoading(false);
    }
  };

  // --- Convocatoria ---
  const cargarConvocatoria = useCallback(
    async (partidoId) => {
      setLoadingConvocatoria(true);
      try {
        const data = await apiFetch("/api/partidos/ver-convocatoria-partido-equipo", {
          params: { partido_id: partidoId },
        });
        setConvocatoria({
          jugadores: Array.isArray(data?.jugadores) ? data.jugadores : [],
          entrenadores: Array.isArray(data?.entrenadores) ? data.entrenadores : [],
        });
      } catch (err) {
        notify.error(err.message);
        setConvocatoria({ jugadores: [], entrenadores: [] });
      } finally {
        setLoadingConvocatoria(false);
      }
    },
    [notify]
  );

  // useCallback: se pasa a AgendaPartidos (memoizado), igual que handleOpenEdit.
  const handleOpenConvocatoria = useCallback(
    async (partido) => {
      setConvocatoriaPartido(partido);
      setSelectedJugador(null);
      setSugerencia(null);
      setOpenConvocatoria(true);
      await cargarConvocatoria(partido._id);
    },
    [cargarConvocatoria]
  );

  // --- Alineación por tiempos (fútbol sala y volley) ---
  const handleOpenAlineacion = useCallback(
    async (partido) => {
      const equipo = equiposById[partido.id_equipo];
      const tiemposIniciales = partido.num_tiempos || numTiemposPorDefecto(equipo?.categoria);

      const construirAlineaciones = (n, existentes) =>
        Array.from({ length: n }, (_, i) => {
          const t = i + 1;
          const encontrada = (existentes || []).find((a) => a.tiempo === t);
          return encontrada || { tiempo: t, alineacion: [] };
        });

      setAlineacionPartido(partido);
      setNumTiempos(tiemposIniciales);
      setModoVolley(partido.modo_volley || MODO_VOLLEY_POR_DEFECTO);
      setTiempoActivo(1);
      setAlineaciones(construirAlineaciones(tiemposIniciales, partido.alineaciones));
      setOpenAlineacion(true);
      setLoadingAlineacion(true);
      try {
        const data = await apiFetch("/api/partidos/ver-convocatoria-partido-equipo", {
          params: { partido_id: partido._id },
        });
        const ids = Array.isArray(data?.jugadores) ? data.jugadores.map((j) => j._id || j.id) : [];
        setConvocadosAlineacion(ids.map((id) => jugadoresById[id]).filter(Boolean));
      } catch (err) {
        notify.error(err.message);
        setConvocadosAlineacion([]);
      } finally {
        setLoadingAlineacion(false);
      }
    },
    [equiposById, jugadoresById, notify]
  );

  const handleCloseAlineacion = () => {
    setOpenAlineacion(false);
    setAlineacionPartido(null);
    setMenuAnchor(null);
    setSlotSeleccionado(null);
  };

  const guardarAlineaciones = async (
    nuevasAlineaciones,
    nuevoNumTiempos,
    nuevoModoVolley = modoVolley
  ) => {
    if (!alineacionPartido) return;
    setSavingSlot(true);
    try {
      await apiFetch("/api/partidos/actualizar-partido-equipo", {
        method: "PUT",
        body: {
          _id: alineacionPartido._id,
          competicion: alineacionPartido.competicion,
          temporada: alineacionPartido.temporada,
          jornada: alineacionPartido.jornada,
          fecha: alineacionPartido.fecha,
          hora: alineacionPartido.hora,
          lugar: alineacionPartido.lugar,
          local: alineacionPartido.local,
          equipo_rival: alineacionPartido.equipo_rival,
          resultado: alineacionPartido.resultado,
          id_equipo: alineacionPartido.id_equipo,
          num_tiempos: nuevoNumTiempos,
          alineaciones: nuevasAlineaciones,
          modo_volley: nuevoModoVolley,
        },
      });
      setPartidos((prev) =>
        prev.map((p) =>
          p._id === alineacionPartido._id
            ? {
                ...p,
                num_tiempos: nuevoNumTiempos,
                alineaciones: nuevasAlineaciones,
                modo_volley: nuevoModoVolley,
              }
            : p
        )
      );
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSavingSlot(false);
    }
  };

  const handleSlotClick = (slot, event) => {
    setSlotSeleccionado(slot);
    setMenuAnchor(event.currentTarget);
  };

  const handleCerrarMenuSlot = () => {
    setMenuAnchor(null);
    setSlotSeleccionado(null);
  };

  const handleAsignarJugador = async (jugadorId) => {
    if (!slotSeleccionado) return;
    const nuevas = alineaciones.map((t) => {
      if (t.tiempo !== tiempoActivo) return t;
      const otras = t.alineacion.filter(
        (a) => a.slot !== slotSeleccionado.id && a.jugador_id !== jugadorId
      );
      return { ...t, alineacion: [...otras, { slot: slotSeleccionado.id, jugador_id: jugadorId }] };
    });
    setAlineaciones(nuevas);
    handleCerrarMenuSlot();
    await guardarAlineaciones(nuevas, numTiempos);
  };

  const handleQuitarSlot = async () => {
    if (!slotSeleccionado) return;
    const nuevas = alineaciones.map((t) =>
      t.tiempo !== tiempoActivo
        ? t
        : { ...t, alineacion: t.alineacion.filter((a) => a.slot !== slotSeleccionado.id) }
    );
    setAlineaciones(nuevas);
    handleCerrarMenuSlot();
    await guardarAlineaciones(nuevas, numTiempos);
  };

  const handleCambiarNumTiempos = async (nuevo) => {
    const completas = Array.from({ length: nuevo }, (_, i) => {
      const t = i + 1;
      const encontrada = alineaciones.find((a) => a.tiempo === t);
      return encontrada || { tiempo: t, alineacion: [] };
    });
    setNumTiempos(nuevo);
    setAlineaciones(completas);
    if (tiempoActivo > nuevo) setTiempoActivo(nuevo);
    await guardarAlineaciones(completas, nuevo);
  };

  const handleCambiarModoVolley = async (nuevo) => {
    setModoVolley(nuevo);
    await guardarAlineaciones(alineaciones, numTiempos, nuevo);
  };

  const esVolleyAlineacion = esVolley(equiposById[alineacionPartido?.id_equipo]?.deporte);
  const slotsAlineacion = esVolleyAlineacion ? slotsVolley(modoVolley) : SLOTS_FUTSAL;

  const alineacionTiempoActivo = useMemo(
    () => alineaciones.find((t) => t.tiempo === tiempoActivo)?.alineacion || [],
    [alineaciones, tiempoActivo]
  );

  const convocadosLibresTiempoActivo = useMemo(
    () =>
      convocadosAlineacion.filter(
        (j) => !alineacionTiempoActivo.some((a) => a.jugador_id === (j._id || j.id))
      ),
    [convocadosAlineacion, alineacionTiempoActivo]
  );

  // Nº de tiempos que ha jugado cada convocado (en cualquier puesto), para ver
  // de un vistazo si el reparto de minutos está siendo equilibrado.
  const tiemposJugadosPorJugador = useMemo(() => {
    const conteo = {};
    alineaciones.forEach((t) => {
      const jugadoresEnEsteTiempo = new Set(
        t.alineacion.filter((a) => a.jugador_id).map((a) => a.jugador_id)
      );
      jugadoresEnEsteTiempo.forEach((id) => {
        conteo[id] = (conteo[id] || 0) + 1;
      });
    });
    return conteo;
  }, [alineaciones]);

  const jugadoresDisponibles = useMemo(() => {
    if (!convocatoriaPartido) return [];
    return jugadores.filter((j) => {
      if (j.equipo_actual !== convocatoriaPartido.id_equipo) return false;
      return !convocatoria.jugadores.some((c) => (c._id || c.id) === j._id);
    });
  }, [jugadores, convocatoriaPartido, convocatoria.jugadores]);

  // Disponibles enriquecidos con cuántas veces han sido convocados en los
  // últimos partidos y a cuántos de los últimos entrenamientos han asistido.
  // Se ordenan para recomendar primero a quien más entrena y menos ha jugado
  // (rotación), dejando los lesionados al final.
  const disponiblesConInfo = useMemo(() => {
    if (!convocatoriaPartido) return [];
    return jugadoresDisponibles
      .map((j) => {
        const { veces, sobre } = contarConvocatoriasRecientes(
          j._id,
          partidos,
          convocatoriaPartido.id_equipo,
          { excluirPartidoId: convocatoriaPartido._id }
        );
        const { veces: asistencias, sobre: sobreEntrenos } =
          contarAsistenciasEntrenamientoRecientes(
            j._id,
            entrenamientos,
            convocatoriaPartido.id_equipo
          );
        return {
          ...j,
          vecesConvocado: veces,
          sobrePartidos: sobre,
          asistenciasEntreno: asistencias,
          sobreEntrenos,
          prioridad: asistencias - veces,
        };
      })
      .sort((a, b) => {
        if (Boolean(a.lesionado) !== Boolean(b.lesionado)) return a.lesionado ? 1 : -1;
        if (a.prioridad !== b.prioridad) return b.prioridad - a.prioridad;
        return (a.nombre || "").localeCompare(b.nombre || "");
      });
  }, [jugadoresDisponibles, partidos, entrenamientos, convocatoriaPartido]);

  const huecosParaSugerir = Math.max(0, MAX_CONVOCADOS_SUGERENCIA - convocatoria.jugadores.length);

  const disponiblesParaSugerir = useMemo(
    () => disponiblesConInfo.filter((j) => !j.lesionado).slice(0, huecosParaSugerir),
    [disponiblesConInfo, huecosParaSugerir]
  );

  // La convocatoria del backend solo trae nombre e id; se completa con el
  // dorsal desde el listado de jugadores ya cargado, para poder mostrarlo y
  // usarlo en el mensaje de WhatsApp.
  const convocadosConNumero = useMemo(
    () =>
      convocatoria.jugadores.map((c) => {
        const id = c._id || c.id;
        const completo = jugadores.find((j) => j._id === id);
        return { ...c, numero: completo?.numero ?? null };
      }),
    [convocatoria.jugadores, jugadores]
  );

  const addJugador = async (jugador) => {
    if (!jugador || !convocatoriaPartido) return;
    try {
      await apiFetch("/api/partidos/actualizar-jugador-convocatoria-equipo", {
        method: "POST",
        params: { jugador_id: jugador._id, partido_id: convocatoriaPartido._id },
      });
      setSelectedJugador(null);
      await cargarConvocatoria(convocatoriaPartido._id);
    } catch (err) {
      notify.error(err.message);
    }
  };

  const addTodos = async () => {
    if (!convocatoriaPartido || jugadoresDisponibles.length === 0) return;
    setLoadingConvocatoria(true);
    try {
      for (const jugador of jugadoresDisponibles) {
        // eslint-disable-next-line no-await-in-loop
        await apiFetch("/api/partidos/actualizar-jugador-convocatoria-equipo", {
          method: "POST",
          params: { jugador_id: jugador._id, partido_id: convocatoriaPartido._id },
        }).catch(() => {});
      }
      notify.success("Convocatoria completada");
    } finally {
      await cargarConvocatoria(convocatoriaPartido._id);
    }
  };

  // "Sugerir convocatoria": propone hasta completar 11 convocados (los que
  // más entrenan y menos han jugado, sin lesionados) pero no llama al backend
  // todavía — el entrenador confirma o descarta antes de guardar nada.
  const sugerirConvocatoria = () => {
    if (huecosParaSugerir <= 0) {
      notify.info(`Ya tienes ${MAX_CONVOCADOS_SUGERENCIA} o más jugadores convocados.`);
      return;
    }
    if (disponiblesParaSugerir.length === 0) {
      notify.info("No quedan jugadores disponibles (sin lesionar) para sugerir.");
      return;
    }
    setSugerencia(disponiblesParaSugerir);
  };

  const quitarDeSugerencia = (jugador) => {
    setSugerencia((prev) => (prev ? prev.filter((j) => j._id !== jugador._id) : prev));
  };

  const descartarSugerencia = () => setSugerencia(null);

  const confirmarSugerencia = async () => {
    if (!convocatoriaPartido || !sugerencia || sugerencia.length === 0) return;
    setLoadingConvocatoria(true);
    try {
      for (const jugador of sugerencia) {
        // eslint-disable-next-line no-await-in-loop
        await apiFetch("/api/partidos/actualizar-jugador-convocatoria-equipo", {
          method: "POST",
          params: { jugador_id: jugador._id, partido_id: convocatoriaPartido._id },
        }).catch(() => {});
      }
      notify.success("Convocatoria confirmada");
      setSugerencia(null);
    } finally {
      await cargarConvocatoria(convocatoriaPartido._id);
    }
  };

  const removeJugador = async (jugador) => {
    const jugadorId = jugador._id || jugador.id;
    if (!jugadorId || !convocatoriaPartido) return;
    try {
      await apiFetch("/api/partidos/eliminar-jugador-convocatoria-equipo", {
        method: "DELETE",
        params: { jugador_id: jugadorId, partido_id: convocatoriaPartido._id },
      });
      await cargarConvocatoria(convocatoriaPartido._id);
    } catch (err) {
      notify.error(err.message);
    }
  };

  const handleCloseConvocatoria = async () => {
    setOpenConvocatoria(false);
    setConvocatoriaPartido(null);
    setSugerencia(null);
    await fetchData();
  };

  // Llegada desde el aviso de "próximo partido" del Dashboard: abre
  // directamente la convocatoria de ese partido.
  useEffect(() => {
    const partidoId = location.state?.openConvocatoriaPartidoId;
    if (!partidoId || partidos.length === 0) return;
    const partido = partidos.find((p) => p._id === partidoId);
    if (partido) {
      handleOpenConvocatoria(partido);
    }
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, partidos]); // eslint-disable-line

  const renderSkeleton = () => (
    <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid", borderColor: "grey.200" }}>
      {[...Array(7)].map((_, i) => (
        <Stack key={i} direction="row" spacing={2} alignItems="center" px={2.5} py={1.5}>
          <Skeleton variant="rounded" width={52} height={52} sx={{ borderRadius: "14px" }} />
          <Box flex={1}>
            <Skeleton variant="text" width="45%" height={22} />
            <Skeleton variant="text" width="30%" height={16} />
          </Box>
          <Skeleton variant="rounded" width={60} height={26} sx={{ borderRadius: 3 }} />
          <Skeleton variant="rounded" width={60} height={26} sx={{ borderRadius: 3 }} />
        </Stack>
      ))}
    </Card>
  );

  const filters = [
    {
      id: "equipo",
      label: "Equipo",
      allLabel: "Todos los equipos",
      value: equipoFiltro,
      onChange: setEquipoFiltro,
      options: equiposTemporada.map((e) => ({ value: e._id || e.id, label: e.nombre })),
      minWidth: 130,
    },
    {
      id: "competicion",
      label: "Competición",
      allLabel: "Todas las competiciones",
      value: competicionFiltro,
      onChange: setCompeticionFiltro,
      options: competiciones.map((c) => ({ value: c, label: c })),
      minWidth: 130,
    },
    {
      id: "estado",
      label: "Estado",
      allLabel: "Todos",
      value: statusFiltro,
      onChange: setStatusFiltro,
      options: [
        { value: "Pendiente", label: "Pendientes" },
        { value: "Jugado", label: "Jugados" },
      ],
      minWidth: 105,
    },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box py={3}>
        <PageToolbar
          icon={SportsSoccerIcon}
          title="Partidos"
          count={filteredPartidos.length}
          countLabel={filteredPartidos.length === 1 ? "partido" : "partidos"}
          action={{
            label: "Nuevo partido",
            shortLabel: "Nuevo",
            icon: <AddIcon />,
            onClick: handleOpenCreate,
          }}
          search={{
            value: searchTerm,
            onChange: setSearchTerm,
            placeholder: "Buscar por rival, competición, lugar o equipo...",
          }}
          filters={filters}
        >
          {userRole === "coordinador" && (
            <Stack direction="row" justifyContent="flex-end" mt={1.5}>
              <Button
                size="small"
                variant="outlined"
                startIcon={syncLoading ? <CircularProgress size={14} /> : <SyncIcon />}
                onClick={handleSincronizar}
                disabled={syncLoading}
                sx={{
                  borderRadius: 2.5,
                  borderColor: "grey.400",
                  color: "text.secondary",
                  "&:hover": { borderColor: "info.main", color: "info.main" },
                }}
              >
                {syncLoading ? "Sincronizando..." : "Sincronizar IMD"}
              </Button>
            </Stack>
          )}
        </PageToolbar>

        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        {loading ? (
          renderSkeleton()
        ) : filteredPartidos.length === 0 ? (
          <EmptyState
            icon={SportsSoccerIcon}
            title="No hay partidos"
            subtitle={
              partidos.length === 0
                ? "Aún no hay partidos registrados. Créalos manualmente o sincroniza con el IMD."
                : "Ningún partido coincide con los filtros o la temporada seleccionada."
            }
            actionLabel="Crear partido"
            onAction={handleOpenCreate}
          />
        ) : (
          <AgendaPartidos
            grupos={gruposPorMes}
            equiposById={equiposById}
            onOpenConvocatoria={handleOpenConvocatoria}
            onOpenAlineacion={handleOpenAlineacion}
            onEdit={handleOpenEdit}
            onDelete={setDeleteTarget}
          />
        )}
      </Box>

      <Fab
        color="primary"
        aria-label="Crear partido"
        onClick={handleOpenCreate}
        sx={{
          position: "fixed",
          bottom: 24,
          right: 24,
          color: "#fff",
          display: { xs: "flex", sm: "none" },
        }}
      >
        <AddIcon />
      </Fab>

      {/* Crear / editar partido */}
      <ModalShell
        open={openModal}
        onClose={() => setOpenModal(false)}
        title={editMode ? "Editar partido" : "Nuevo partido"}
        subtitle={
          editMode
            ? `vs ${currentPartido.equipo_rival || ""}`
            : `Temporada ${currentPartido.temporada || temporada}`
        }
        icon={SportsSoccerIcon}
        actions={
          <>
            <Button onClick={() => setOpenModal(false)} sx={{ color: "text.secondary" }}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              startIcon={<SaveIcon />}
              disabled={
                saving ||
                !currentPartido.equipo_rival ||
                !currentPartido.competicion ||
                (!editMode &&
                  (userRole === "coordinador" || equiposTemporada.length > 1) &&
                  !currentPartido.id_equipo)
              }
              sx={{ borderRadius: 2 }}
            >
              {saving ? "Guardando..." : editMode ? "Guardar cambios" : "Crear partido"}
            </Button>
          </>
        }
      >
        <Stack spacing={2.5} mt={0.5}>
          {(userRole === "coordinador" || equiposTemporada.length > 1) && (
            <FormControl fullWidth required={!editMode}>
              <InputLabel>Equipo propio</InputLabel>
              <Select
                name="id_equipo"
                value={currentPartido.id_equipo || ""}
                label="Equipo propio"
                onChange={handleChange}
                sx={{ height: 44 }}
              >
                {equiposTemporada.map((eq) => (
                  <MenuItem key={eq._id} value={eq._id}>
                    {eq.nombre}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <TextField
            label="Equipo rival"
            name="equipo_rival"
            value={currentPartido.equipo_rival}
            onChange={handleChange}
            fullWidth
            required
          />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Competición"
                name="competicion"
                value={currentPartido.competicion}
                onChange={handleChange}
                fullWidth
                required
                placeholder="Ej: IMD"
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                label="Jornada"
                name="jornada"
                type="number"
                value={currentPartido.jornada}
                onChange={handleChange}
                fullWidth
                inputProps={{ min: 1, max: 30 }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <FormControl fullWidth>
                <InputLabel>Temporada</InputLabel>
                <Select
                  name="temporada"
                  value={currentPartido.temporada || ""}
                  label="Temporada"
                  onChange={handleChange}
                  sx={{ height: 44 }}
                >
                  {temporadasSeleccionables.map((t) => (
                    <MenuItem key={t} value={t}>
                      {t}/{String(Number(t) + 1).slice(-2)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                label="Fecha"
                name="fecha"
                type="date"
                value={currentPartido.fecha || ""}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Hora"
                name="hora"
                type="time"
                value={currentPartido.hora || ""}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
          <TextField
            label="Lugar"
            name="lugar"
            value={currentPartido.lugar || ""}
            onChange={handleChange}
            fullWidth
            placeholder="Pabellón, dirección..."
          />
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={6}>
              <TextField
                label="Resultado"
                name="resultado"
                value={currentPartido.resultado || ""}
                onChange={handleChange}
                fullWidth
                placeholder="Ej: 3-2"
                helperText="Vacío si aún no se ha jugado"
              />
            </Grid>
            <Grid item xs={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={Boolean(currentPartido.local)}
                    onChange={handleChange}
                    name="local"
                  />
                }
                label="Jugamos en casa"
              />
            </Grid>
          </Grid>
        </Stack>
      </ModalShell>

      {/* Convocatoria */}
      <ModalShell
        open={openConvocatoria}
        onClose={handleCloseConvocatoria}
        title="Convocatoria"
        subtitle={
          convocatoriaPartido
            ? `vs ${convocatoriaPartido.equipo_rival} · ${formatDate(convocatoriaPartido.fecha)}`
            : ""
        }
        icon={GroupIcon}
        color="info"
        maxWidth="md"
        actions={
          <>
            <Button onClick={handleCloseConvocatoria} sx={{ color: "text.secondary" }}>
              Cerrar
            </Button>
            <Button
              variant="contained"
              color="info"
              startIcon={<ShareIcon />}
              onClick={() => setOpenShare(true)}
              disabled={convocatoria.jugadores.length === 0}
              sx={{ borderRadius: 2 }}
            >
              Compartir por WhatsApp
            </Button>
          </>
        }
      >
        <Grid container spacing={2.5}>
          <Grid item xs={12} md={6}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography variant="subtitle2" fontWeight={700}>
                Convocados ({convocatoria.jugadores.length}/
                {convocatoria.jugadores.length + jugadoresDisponibles.length})
              </Typography>
            </Stack>
            {loadingConvocatoria ? (
              <Stack spacing={1}>
                {[...Array(4)].map((_, i) => (
                  <Skeleton key={i} variant="rounded" height={44} sx={{ borderRadius: 2 }} />
                ))}
              </Stack>
            ) : convocadosConNumero.length ? (
              <List dense disablePadding sx={{ maxHeight: 360, overflowY: "auto" }}>
                {convocadosConNumero.map((j) => (
                  <ListItem
                    key={j._id || j.id}
                    sx={{
                      border: "1px solid",
                      borderColor: "grey.200",
                      borderRadius: 2.5,
                      mb: 0.75,
                      pr: 6,
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar
                        sx={{ width: 32, height: 32, fontSize: "0.8rem", bgcolor: "info.main" }}
                      >
                        {j.numero ?? (j.nombre || "?")[0]}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={j.nombre}
                      primaryTypographyProps={{ fontSize: "0.875rem", fontWeight: 600 }}
                    />
                    <ListItemSecondaryAction>
                      <Tooltip title="Quitar de la convocatoria">
                        <IconButton
                          size="small"
                          onClick={() => removeJugador(j)}
                          sx={{ color: "text.secondary", "&:hover": { color: "error.main" } }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary" py={2}>
                Aún no hay jugadores convocados.
              </Typography>
            )}
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" fontWeight={700} mb={1}>
              Añadir jugadores
            </Typography>
            <Stack spacing={1.5}>
              <Autocomplete
                value={selectedJugador}
                onChange={(e, v) => v && addJugador(v)}
                options={jugadoresDisponibles}
                getOptionLabel={(o) => o.nombre || ""}
                isOptionEqualToValue={(o, v) => o._id === v._id}
                renderInput={(params) => (
                  <TextField {...params} label="Buscar jugador" size="small" />
                )}
                noOptionsText="No quedan jugadores disponibles"
              />

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <Tooltip
                  title={`Propone hasta completar ${MAX_CONVOCADOS_SUGERENCIA} convocados: prioriza a quien más entrena y menos ha jugado, sin lesionados. No se guarda hasta que la confirmes.`}
                >
                  <span style={{ flex: 1 }}>
                    <Button
                      variant="contained"
                      color="info"
                      startIcon={<GroupAddIcon />}
                      onClick={sugerirConvocatoria}
                      disabled={loadingConvocatoria || Boolean(sugerencia)}
                      fullWidth
                      sx={{ borderRadius: 2.5 }}
                    >
                      Sugerir convocatoria
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title="Añade a todos los disponibles ahora mismo, incluidos los lesionados">
                  <span style={{ flex: 1 }}>
                    <Button
                      variant="outlined"
                      startIcon={<GroupAddIcon />}
                      onClick={addTodos}
                      disabled={jugadoresDisponibles.length === 0 || loadingConvocatoria}
                      fullWidth
                      sx={{
                        borderRadius: 2.5,
                        borderColor: "grey.400",
                        color: "text.primary",
                        "&:hover": { borderColor: "info.main", color: "info.main" },
                      }}
                    >
                      Convocar a todos ({jugadoresDisponibles.length})
                    </Button>
                  </span>
                </Tooltip>
              </Stack>

              {sugerencia ? (
                <Box
                  sx={{
                    bgcolor: "rgba(26,115,232,.06)",
                    border: "1px solid",
                    borderColor: "info.main",
                    borderRadius: 2.5,
                    p: 1.5,
                  }}
                >
                  <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                    <Typography variant="subtitle2" fontWeight={700} color="info.main">
                      Sugerencia ({sugerencia.length}/{MAX_CONVOCADOS_SUGERENCIA})
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Quita a quien no quieras antes de confirmar
                    </Typography>
                  </Stack>
                  {sugerencia.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" py={1}>
                      Has quitado a todos los sugeridos.
                    </Typography>
                  ) : (
                    <List dense disablePadding sx={{ maxHeight: 240, overflowY: "auto", mb: 1 }}>
                      {sugerencia.map((j) => (
                        <ListItem
                          key={j._id}
                          sx={{
                            bgcolor: "#fff",
                            borderRadius: 2,
                            mb: 0.5,
                            pr: 6,
                          }}
                        >
                          <ListItemAvatar>
                            <Avatar
                              sx={{
                                width: 28,
                                height: 28,
                                fontSize: "0.7rem",
                                bgcolor: "info.main",
                              }}
                            >
                              {j.numero ?? (j.nombre || "?")[0]}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={j.nombre}
                            secondary={`${j.asistenciasEntreno}/${j.sobreEntrenos} entrenos · ${j.vecesConvocado}/${j.sobrePartidos} partidos`}
                            primaryTypographyProps={{ fontSize: "0.82rem", fontWeight: 600 }}
                            secondaryTypographyProps={{ fontSize: "0.68rem" }}
                          />
                          <ListItemSecondaryAction>
                            <IconButton size="small" onClick={() => quitarDeSugerencia(j)}>
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  )}
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      onClick={descartarSugerencia}
                      sx={{ color: "text.secondary" }}
                    >
                      Descartar
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      color="info"
                      onClick={confirmarSugerencia}
                      disabled={sugerencia.length === 0 || loadingConvocatoria}
                      sx={{ borderRadius: 2 }}
                    >
                      Confirmar convocatoria ({sugerencia.length})
                    </Button>
                  </Stack>
                </Box>
              ) : (
                <List dense disablePadding sx={{ maxHeight: 280, overflowY: "auto" }}>
                  {disponiblesConInfo.map((j) => (
                    <ListItem
                      key={j._id}
                      button
                      onClick={() => addJugador(j)}
                      sx={{
                        borderRadius: 2.5,
                        mb: 0.5,
                        opacity: j.lesionado ? 0.6 : 1,
                        "&:hover": { bgcolor: "rgba(26,115,232,.06)" },
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar
                          sx={{
                            width: 30,
                            height: 30,
                            fontSize: "0.75rem",
                            bgcolor: j.lesionado ? "error.main" : "grey.400",
                          }}
                        >
                          {j.numero ?? (j.nombre || "?")[0]}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={j.nombre}
                        secondary={
                          j.lesionado
                            ? "Lesionado"
                            : `${j.asistenciasEntreno}/${j.sobreEntrenos} entrenos · ${j.vecesConvocado}/${j.sobrePartidos} partidos`
                        }
                        primaryTypographyProps={{ fontSize: "0.85rem", fontWeight: 600 }}
                        secondaryTypographyProps={{
                          fontSize: "0.7rem",
                          color: j.lesionado ? "error.main" : "text.secondary",
                          fontWeight: j.lesionado ? 700 : 400,
                        }}
                      />
                      {j.lesionado ? (
                        <Tooltip title="Lesionado — se puede convocar igualmente si hace falta">
                          <LocalHospitalIcon sx={{ fontSize: 18, color: "error.main" }} />
                        </Tooltip>
                      ) : (
                        <PersonAddIcon sx={{ fontSize: 18, color: "info.main" }} />
                      )}
                    </ListItem>
                  ))}
                </List>
              )}
            </Stack>
          </Grid>
        </Grid>
      </ModalShell>

      <ShareConvocatoriaDialog
        open={openShare}
        onClose={() => setOpenShare(false)}
        partido={convocatoriaPartido}
        equipoNombre={getEquipoName(convocatoriaPartido?.id_equipo)}
        convocados={convocadosConNumero}
      />

      {/* Alineación por tiempos (fútbol sala) */}
      <ModalShell
        open={openAlineacion}
        onClose={handleCloseAlineacion}
        title="Alineación"
        subtitle={
          alineacionPartido
            ? `vs ${alineacionPartido.equipo_rival} · ${formatDate(alineacionPartido.fecha)}`
            : ""
        }
        icon={GridOnIcon}
        color="primary"
        maxWidth="sm"
        actions={
          <Button onClick={handleCloseAlineacion} sx={{ color: "text.secondary" }}>
            Cerrar
          </Button>
        }
      >
        {loadingAlineacion ? (
          <Skeleton variant="rounded" height={260} sx={{ borderRadius: 3 }} />
        ) : (
          <Stack spacing={2}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              gap={1}
            >
              <Stack direction="row" spacing={0.75} alignItems="center">
                <Tooltip title="Tiempo anterior">
                  <span>
                    <IconButton
                      size="small"
                      disabled={tiempoActivo <= 1}
                      onClick={() => setTiempoActivo((t) => Math.max(1, t - 1))}
                    >
                      <ChevronLeftIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                {Array.from({ length: numTiempos }, (_, i) => i + 1).map((t) => (
                  <Chip
                    key={t}
                    label={`Tiempo ${t}`}
                    size="small"
                    onClick={() => setTiempoActivo(t)}
                    color={t === tiempoActivo ? "primary" : "default"}
                    variant={t === tiempoActivo ? "filled" : "outlined"}
                    sx={{ fontWeight: 700 }}
                  />
                ))}
                <Tooltip title="Siguiente tiempo">
                  <span>
                    <IconButton
                      size="small"
                      disabled={tiempoActivo >= numTiempos}
                      onClick={() => setTiempoActivo((t) => Math.min(numTiempos, t + 1))}
                    >
                      <ChevronRightIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>

              <Stack direction="row" spacing={1}>
                {esVolleyAlineacion && (
                  <FormControl size="small" sx={{ minWidth: 130 }}>
                    <InputLabel id="modo-volley-label">Jugadores en pista</InputLabel>
                    <Select
                      labelId="modo-volley-label"
                      label="Jugadores en pista"
                      value={modoVolley}
                      onChange={(e) => handleCambiarModoVolley(Number(e.target.value))}
                    >
                      <MenuItem value={4}>4 jugadores</MenuItem>
                      <MenuItem value={6}>6 jugadores</MenuItem>
                    </Select>
                  </FormControl>
                )}

                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel id="num-tiempos-label">Nº de tiempos</InputLabel>
                  <Select
                    labelId="num-tiempos-label"
                    label="Nº de tiempos"
                    value={numTiempos}
                    onChange={(e) => handleCambiarNumTiempos(Number(e.target.value))}
                  >
                    <MenuItem value={2}>2 tiempos</MenuItem>
                    <MenuItem value={4}>4 tiempos</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
            </Stack>

            {esVolleyAlineacion ? (
              <PistaVolley
                slots={slotsAlineacion}
                asignaciones={alineacionTiempoActivo}
                jugadoresById={jugadoresById}
                onSlotClick={handleSlotClick}
              />
            ) : (
              <MedioCampoFutsal
                slots={slotsAlineacion}
                asignaciones={alineacionTiempoActivo}
                jugadoresById={jugadoresById}
                onSlotClick={handleSlotClick}
              />
            )}

            <Typography variant="caption" color="text.secondary">
              {alineacionTiempoActivo.length}/{slotsAlineacion.length} puestos cubiertos en el
              tiempo {tiempoActivo}. Toca un círculo para asignar o quitar jugador
              {savingSlot ? " · guardando…" : ""}.
            </Typography>

            {convocadosAlineacion.length > 0 && (
              <Stack spacing={0.75}>
                <Typography
                  variant="caption"
                  fontWeight={700}
                  color="text.secondary"
                  textTransform="uppercase"
                  letterSpacing="0.05em"
                  fontSize="0.68rem"
                >
                  Tiempos jugados
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={0.75}>
                  {convocadosAlineacion.map((j) => {
                    const jugados = tiemposJugadosPorJugador[j._id || j.id] || 0;
                    return (
                      <Chip
                        key={j._id || j.id}
                        label={`${j.nombre.split(" ")[0]} · ${jugados}/${numTiempos}`}
                        size="small"
                        color={jugados === 0 ? "default" : "info"}
                        variant={jugados === 0 ? "outlined" : "filled"}
                        sx={{ fontWeight: 600 }}
                      />
                    );
                  })}
                </Stack>
              </Stack>
            )}

            {convocadosAlineacion.length === 0 && (
              <Alert severity="warning" sx={{ borderRadius: 2 }}>
                No hay jugadores convocados para este partido. Añádelos primero en
                &quot;Convocatoria&quot;.
              </Alert>
            )}
          </Stack>
        )}
      </ModalShell>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleCerrarMenuSlot}>
        {alineacionTiempoActivo.some((a) => a.slot === slotSeleccionado?.id) && (
          <MenuItem onClick={handleQuitarSlot}>
            <ListItemIcon>
              <CloseIcon fontSize="small" />
            </ListItemIcon>
            Quitar de este puesto
          </MenuItem>
        )}
        {convocadosLibresTiempoActivo.length === 0 ? (
          <MenuItem disabled>No quedan convocados libres</MenuItem>
        ) : (
          convocadosLibresTiempoActivo.map((j) => (
            <MenuItem key={j._id || j.id} onClick={() => handleAsignarJugador(j._id || j.id)}>
              {j.numero != null ? `#${j.numero} ` : ""}
              {j.nombre}
            </MenuItem>
          ))
        )}
      </Menu>

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Eliminar partido"
        message={
          <>
            ¿Seguro que quieres eliminar el partido contra <b>{deleteTarget?.equipo_rival}</b> (
            {formatDate(deleteTarget?.fecha)})? Esta acción no se puede deshacer.
          </>
        }
        onConfirm={handleDelete}
        onClose={() => setDeleteTarget(null)}
        loading={saving}
      />

      <Footer />
    </DashboardLayout>
  );
}
